import {Button, Card, Checkbox, Choice} from 'platform/components';
import {Grid, HStack, Show, VStack} from 'platform/foundation';

import {SaveSaleVehicleFinancingBody} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {COUNTRY_DEU, useTenant} from '@dms/shared';

import {FINANCING_COST_OVERRIDES} from './constants/FINANCING_COST_OVERRIDES';
import {FinancingIncludableOverride} from './FinancingIncludableOverride';
import {MoneyInputField} from './MoneyInputField';
import {FinancingTemplateDraft} from './types/FinancingTemplateDraft';
import {countVisibleFinancingOverrides} from './utils/financingOverrides/countVisibleFinancingOverrides';
import {getOverrideLabel} from './utils/financingOverrides/getOverrideLabel';
import {isMoneyOverride} from './utils/financingOverrides/isMoneyOverride';
import {isScalarOverride} from './utils/financingOverrides/isScalarOverride';
import {parseInterestRateType} from './utils/financingOverrides/parseInterestRateType';
import {resetVisibleFinancingOverrides} from './utils/financingOverrides/resetVisibleFinancingOverrides';
import {resolveMoneyOverride} from './utils/financingOverrides/resolveMoneyOverride';
import {resolveScalarOverride} from './utils/financingOverrides/resolveScalarOverride';
import {withFinancingBodyField} from './utils/financingOverrides/withFinancingBodyField';

interface FinancingOverridesProps {
  vehicleFinancingBody: SaveSaleVehicleFinancingBody;
  template: FinancingTemplateDraft | undefined;
  currency: string;
  isReadOnly: boolean;
  onOpenConfiguration?: () => void;
  onChange: (vehicleFinancingBody: SaveSaleVehicleFinancingBody) => void;
}

export function FinancingOverrides(props: FinancingOverridesProps) {
  const {isTenantCountrySupported} = useTenant();
  const isGermanTenant = isTenantCountrySupported(COUNTRY_DEU);
  const overriddenCount = countVisibleFinancingOverrides(
    props.vehicleFinancingBody,
    isGermanTenant
  );
  const resetOverrides = () =>
    props.onChange(resetVisibleFinancingOverrides(props.vehicleFinancingBody, isGermanTenant));

  return (
    <VStack spacing={4}>
      <Card
        title={i18n.t('page.vehicle.financingAndLeasing.fees')}
        titleIcon="maps/local_offer"
        variant="inlineGrey"
        isExpandable
        isClosedByDefault
        hasSeparator
      >
        <VStack spacing={4}>
          <Grid columns={[1, 2, 4]} data-testid="financing-general-overrides">
            <MoneyInputField
              label={getOverrideLabel(
                i18n.t('page.vehicle.financingAndLeasing.processingFee'),
                isMoneyOverride(
                  props.vehicleFinancingBody.processingFeeOverride,
                  props.template?.processingFee
                )
              )}
              value={
                props.vehicleFinancingBody.processingFeeOverride ??
                props.template?.processingFee ??
                null
              }
              currency={props.currency}
              isDisabled={props.isReadOnly}
              resetLabel={i18n.t('entity.vehicleFinancingTemplate.actions.restoreDefault')}
              onReset={
                props.vehicleFinancingBody.processingFeeOverride !== null
                  ? () =>
                      props.onChange({...props.vehicleFinancingBody, processingFeeOverride: null})
                  : undefined
              }
              onChange={(value) =>
                props.onChange({
                  ...props.vehicleFinancingBody,
                  processingFeeOverride: resolveMoneyOverride(value, props.template?.processingFee),
                })
              }
            />
            <Show when={isGermanTenant}>
              <Choice
                label={getOverrideLabel(
                  i18n.t('page.vehicle.financingAndLeasing.interestRateType'),
                  isScalarOverride(
                    props.vehicleFinancingBody.interestRateTypeOverride,
                    // A manual configuration stores the fixed rate it defaults to, so only
                    // another type is custom there.
                    props.vehicleFinancingBody.mode === 'MANUAL'
                      ? 'FIXED'
                      : props.template?.interestRateType
                  )
                )}
                value={
                  props.vehicleFinancingBody.interestRateTypeOverride ??
                  props.template?.interestRateType ??
                  'FIXED'
                }
                options={[
                  {
                    value: 'FIXED',
                    label: i18n.t('page.vehicle.financingAndLeasing.fixedInterestRate'),
                  },
                  {
                    value: 'VARIABLE',
                    label: i18n.t('page.vehicle.financingAndLeasing.variableInterestRate'),
                  },
                  {
                    value: 'COMBINED',
                    label: i18n.t('page.vehicle.financingAndLeasing.combinedInterestRate'),
                  },
                ]}
                isNotClearable
                isDisabled={props.isReadOnly}
                onChange={(value) => {
                  const interestRateType = parseInterestRateType(value);

                  props.onChange({
                    ...props.vehicleFinancingBody,
                    interestRateTypeOverride:
                      interestRateType === null
                        ? null
                        : resolveScalarOverride(interestRateType, props.template?.interestRateType),
                  });
                }}
              />
            </Show>
          </Grid>
          <Show when={isGermanTenant}>
            <Grid columns={[1, 2, 4]} data-testid="financing-cost-overrides">
              {FINANCING_COST_OVERRIDES.map((override) => (
                <FinancingIncludableOverride
                  key={override.includedOverrideKey}
                  label={override.getLabel()}
                  included={
                    props.vehicleFinancingBody[override.includedOverrideKey] ??
                    props.template?.[override.templateIncludedKey] ??
                    false
                  }
                  amount={
                    props.vehicleFinancingBody[override.amountOverrideKey] ??
                    props.template?.[override.templateAmountKey] ??
                    null
                  }
                  currency={props.currency}
                  isReadOnly={props.isReadOnly}
                  isAmountOverridden={isMoneyOverride(
                    props.vehicleFinancingBody[override.amountOverrideKey],
                    props.template?.[override.templateAmountKey]
                  )}
                  isIncludedOverridden={isScalarOverride(
                    props.vehicleFinancingBody[override.includedOverrideKey],
                    props.template?.[override.templateIncludedKey]
                  )}
                  onIncludedChange={(value) =>
                    props.onChange(
                      withFinancingBodyField(
                        props.vehicleFinancingBody,
                        override.includedOverrideKey,
                        resolveScalarOverride(value, props.template?.[override.templateIncludedKey])
                      )
                    )
                  }
                  onAmountReset={() =>
                    props.onChange(
                      withFinancingBodyField(
                        props.vehicleFinancingBody,
                        override.amountOverrideKey,
                        null
                      )
                    )
                  }
                  onAmountChange={(value) =>
                    props.onChange(
                      withFinancingBodyField(
                        props.vehicleFinancingBody,
                        override.amountOverrideKey,
                        resolveMoneyOverride(value, props.template?.[override.templateAmountKey])
                      )
                    )
                  }
                />
              ))}
            </Grid>
          </Show>
          <HStack justify="space-between" wrap>
            <Button
              title={i18n.t('page.vehicle.financingAndLeasing.resetAllOverrides')}
              variant="secondary"
              isDisabled={props.isReadOnly || overriddenCount === 0}
              onClick={resetOverrides}
            />
            <Show when={props.onOpenConfiguration}>
              <Button
                title={i18n.t('page.vehicle.financingAndLeasing.openConfiguration')}
                leftIcon="action/settings"
                isDisabled={props.isReadOnly}
                onClick={() => props.onOpenConfiguration?.()}
              />
            </Show>
          </HStack>
        </VStack>
      </Card>

      <Card
        title={i18n.t('page.vehicle.financingAndLeasing.addOns')}
        titleIcon="action/credit_card"
        variant="inlineGrey"
        isExpandable
        isClosedByDefault
        hasSeparator
      >
        <VStack spacing={4}>
          <HStack spacing={4} wrap data-testid="financing-add-ons">
            <Show when={isGermanTenant}>
              <Checkbox
                label={getOverrideLabel(
                  i18n.t('page.vehicle.financingAndLeasing.tradeInPossible'),
                  isScalarOverride(
                    props.vehicleFinancingBody.tradeInPossibleOverride,
                    props.template?.tradeInPossible
                  )
                )}
                value={
                  props.vehicleFinancingBody.tradeInPossibleOverride ??
                  props.template?.tradeInPossible ??
                  false
                }
                isDisabled={props.isReadOnly}
                onChange={(value) =>
                  props.onChange({
                    ...props.vehicleFinancingBody,
                    tradeInPossibleOverride: resolveScalarOverride(
                      value,
                      props.template?.tradeInPossible
                    ),
                  })
                }
              />
            </Show>
            <Show when={isGermanTenant}>
              <Checkbox
                label={i18n.t('page.vehicle.financingAndLeasing.creditProtectionIncluded')}
                value={props.vehicleFinancingBody.creditProtectionIncluded}
                isDisabled={props.isReadOnly}
                onChange={(creditProtectionIncluded) =>
                  props.onChange({...props.vehicleFinancingBody, creditProtectionIncluded})
                }
              />
            </Show>
            <Checkbox
              label={i18n.t('page.vehicle.financingAndLeasing.insuranceIncludedInCalculation')}
              value={props.vehicleFinancingBody.insuranceIncludedInCalculation}
              isDisabled={props.isReadOnly}
              onChange={(insuranceIncludedInCalculation) =>
                props.onChange({...props.vehicleFinancingBody, insuranceIncludedInCalculation})
              }
            />
          </HStack>
          <Show when={props.vehicleFinancingBody.insuranceIncludedInCalculation}>
            <Grid columns={[1, 2, 4]}>
              <MoneyInputField
                label={i18n.t('page.vehicle.financingAndLeasing.liabilityMonthly')}
                tooltip={i18n.t('page.vehicle.financingAndLeasing.hints.liabilityMonthly')}
                value={props.vehicleFinancingBody.liabilityInsuranceMonthly}
                currency={props.currency}
                isDisabled={props.isReadOnly}
                onChange={(liabilityInsuranceMonthly) =>
                  props.onChange({...props.vehicleFinancingBody, liabilityInsuranceMonthly})
                }
              />
              <MoneyInputField
                label={i18n.t('page.vehicle.financingAndLeasing.liabilityDeductible')}
                tooltip={i18n.t('page.vehicle.financingAndLeasing.hints.liabilityDeductible')}
                value={props.vehicleFinancingBody.liabilityInsuranceDeductible}
                currency={props.currency}
                isDisabled={props.isReadOnly}
                onChange={(liabilityInsuranceDeductible) =>
                  props.onChange({...props.vehicleFinancingBody, liabilityInsuranceDeductible})
                }
              />
              <MoneyInputField
                label={i18n.t('page.vehicle.financingAndLeasing.comprehensiveMonthly')}
                tooltip={i18n.t('page.vehicle.financingAndLeasing.hints.comprehensiveMonthly')}
                value={props.vehicleFinancingBody.comprehensiveInsuranceMonthly}
                currency={props.currency}
                isDisabled={props.isReadOnly}
                onChange={(comprehensiveInsuranceMonthly) =>
                  props.onChange({...props.vehicleFinancingBody, comprehensiveInsuranceMonthly})
                }
              />
              <MoneyInputField
                label={i18n.t('page.vehicle.financingAndLeasing.comprehensiveDeductible')}
                tooltip={i18n.t('page.vehicle.financingAndLeasing.hints.comprehensiveDeductible')}
                value={props.vehicleFinancingBody.comprehensiveInsuranceDeductible}
                currency={props.currency}
                isDisabled={props.isReadOnly}
                onChange={(comprehensiveInsuranceDeductible) =>
                  props.onChange({...props.vehicleFinancingBody, comprehensiveInsuranceDeductible})
                }
              />
            </Grid>
          </Show>
        </VStack>
      </Card>
    </VStack>
  );
}
